import { useMemo, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Checkbox,
  Combobox,
  ComboboxItemText,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Text,
} from '@backstage/ui';
import { UserOption } from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import sharedStyles from './SonarQubeServiceGroupPage.module.css';
import styles from "./AddGroupMemberDialog.module.css";

interface AddGroupMemberDialogProps {
  open: boolean;
  serviceGroupId: string;
  userOptions: UserOption[] | undefined;
  loadingUsers: boolean;
  /** Entra ids already in a group; they aren't offered. */
  existingEntraIds: Set<string>;
  /** Entra ids of the product team's members; only they are offered. */
  teamMemberEntraIds: string[] | undefined;
  onClose: () => void;
  onAdded: () => void;
}

export const AddGroupMemberDialog = ({
  open,
  serviceGroupId,
  userOptions,
  loadingUsers,
  existingEntraIds,
  teamMemberEntraIds,
  onClose,
  onAdded,
}: AddGroupMemberDialogProps) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const [memberKey, setMemberKey] = useState<string | null>(null);
  const [makeAdmin, setMakeAdmin] = useState(false);

  // Members are addressed by Entra id, so users without one (not synced from Entra) can't be added.
  const items = useMemo(() => {
    const teamEntraIds = new Set(teamMemberEntraIds);
    return (userOptions ?? [])
      .filter(
        u =>
          !!u.entraId &&
          teamEntraIds.has(u.entraId) &&
          !existingEntraIds.has(u.entraId),
      )
      .map(u => ({ ...u, id: u.ref }));
  }, [userOptions, existingEntraIds, teamMemberEntraIds]);
  const member = items.find(u => u.id === memberKey);

  const [submitState, submit] = useAsyncFn(async () => {
    if (!member?.entraId) return;
    await sonarqubeApi.addUserToGroup(
      serviceGroupId,
      makeAdmin ? 'admin' : 'developer',
      member.entraId,
    );
    setMemberKey(null);
    setMakeAdmin(false);
    onAdded();
  }, [sonarqubeApi, serviceGroupId, member, makeAdmin, onAdded]);

  const close = () => {
    if (submitState.loading) return;
    submitState.error = undefined;
    setMemberKey(null);
    setMakeAdmin(false);
    onClose();
  };

  return (
    <Dialog
      isOpen={open}
      onOpenChange={isOpen => {
        if (!isOpen) close();
      }}
      width={480}
    >
      <DialogHeader>Add group member</DialogHeader>
      <DialogBody className={sharedStyles.dialogBody}>
        <Combobox
          items={items}
          selectedKey={memberKey}
          onSelectionChange={key => setMemberKey(key as string | null)}
          loading={{ state: loadingUsers ? 'loading' : 'idle' }}
          isDisabled={submitState.loading}
          label="Person to add"
          placeholder="Search for a person"
        >
          {item => (
            <ComboboxItemText
              key={item.id}
              title={item.label}
              textValue={item.label}
            />
          )}
        </Combobox>
        <div>
          <Checkbox
            isSelected={makeAdmin}
            onChange={setMakeAdmin}
            isDisabled={submitState.loading}
          >
            Admin rights
          </Checkbox>
          <Text as="p" variant="body-small" color="secondary">
            Admins can add and remove members of this tooling group.
          </Text>
        </div>
        {submitState.error && (
          <Text color="danger" as="p" className={styles.textDanger}>
            {submitState.error.message}
          </Text>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          variant="secondary"
          onClick={close}
          isDisabled={submitState.loading}
        >
          Cancel
        </Button>
        <Button
          onClick={submit}
          isDisabled={submitState.loading || !member}
          isPending={submitState.loading}
        >
          Add member
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
